/** W73b (D-279): Archiv im Tab Akten. Ziel ist die Blaettern-Seite, die eine Kategorie in `?kategorie=<id>` traegt
 *  (Blaettern.tsx); die Anzahl je Kategorie kommt aus `gesamt` von `GET /api/dokumente` mit `status=archiviert` und
 *  `pro_seite=1`, also ohne die Briefe selbst zu laden. W73c (D-280 e): liefert `GET /api/kategorien` je Kategorie
 *  `anzahl_archiviert` (W73c-Server), entfallen die Abfragen je Kategorie; die Zahl „alle“ bleibt eine Abfrage, weil
 *  abgelegte Briefe ohne Kategorie in keiner Kategorie zaehlen. */
import { useQueries } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Dokument, Kategorie, Liste } from "../api/typen";

/** Weg ins Archiv, mit Kategorie vorgewaehlt (nur ihre abgelegten Briefe, W73c) oder (ohne) mit allen Kategorien. */
export function archivWeg(kategorieId?: string): string {
  return kategorieId ? `/blaettern?${new URLSearchParams({ kategorie: kategorieId })}` : "/blaettern";
}

/** W73c (D-288 H4): „Alle abgelegten Briefe“ in Akten fuehrt auf die Liste aller abgelegten Briefe, nicht auf die
 *  Kategorien (Blaettern mit `status=archiviert`). */
export const ALLE_ABGELEGT = "/blaettern?status=archiviert";

/** W73c (D-288 H1): Herkunft im Link-State; wer aus Akten ins Archiv springt, bleibt mobil im Tab Akten (Schale). */
export const AUS_AKTEN = { herkunft: "/akten" } as const;

/** Herkunft aus dem Zustand eines Orts (`location.state`), sonst undefined. */
export function herkunft(zustand: unknown): string | undefined {
  const h = (zustand as { herkunft?: unknown } | null)?.herkunft;
  return typeof h === "string" ? h : undefined;
}

/** W82-3 (F91, D-456 „Archiv zurück nach Akten“): Rückweg des Archivs nach Herkunft. Aus Akten (`AUS_AKTEN`) führt er
 *  zurück nach Akten, sonst (Start mit „Alle ›“, Suche, direkt) zum Start, wie der aktive Tab (Schale `aktiverTab`). */
export function archivRueckweg(zustand: unknown): { zu: string; text: string } {
  return herkunft(zustand) === AUS_AKTEN.herkunft ? { zu: "/akten", text: "Akten" } : { zu: "/", text: "Start" };
}

/** Woher die Zahl je Kategorie kommt: aus `anzahl_archiviert` der Kategorien, wenn jede sie traegt, sonst je eine Abfrage. */
export function zahlQuelle(kategorien: Kategorie[] | undefined): "kategorien" | "abfragen" {
  return kategorien?.length && kategorien.every((k) => typeof k.anzahl_archiviert === "number") ? "kategorien" : "abfragen";
}

/** Welche Kategorien das Archiv zeigt: alle, solange keine Zahl ueber 0 bekannt ist; sonst nur die mit Briefen oder ohne
 *  bekannte Zahl (laedt noch, Fehler). W73c (D-288 H2). */
export function sichtbareKategorien(kategorien: Kategorie[], je: Map<string, number | undefined>): Kategorie[] {
  if (!kategorien.some((k) => (je.get(k.id) ?? 0) > 0)) return kategorien;
  return kategorien.filter((k) => je.get(k.id) !== 0);
}

/** „1 Brief“, „3 Briefe“ */
export function briefe(anzahl: number): string {
  return anzahl === 1 ? "1 Brief" : `${anzahl} Briefe`;
}

/** Vorlesename der Zeile einer Kategorie: „Kategorie Wohnen, 3 Briefe“; solange die Zahl fehlt, nur der Name. */
export function kategorieLabel(name: string, anzahl: number | undefined): string {
  return anzahl === undefined ? `Kategorie ${name}` : `Kategorie ${name}, ${briefe(anzahl)}`;
}

/** Abfrage der Zahl abgelegter Briefe (eine Kategorie oder alle): nur `gesamt` zaehlt, ein Eintrag kommt mit. */
export function anzahlPfad(kategorieId?: string): string {
  const q = new URLSearchParams({ status: "archiviert", pro_seite: "1" });
  if (kategorieId) q.set("kategorie_id", kategorieId);
  return `/dokumente?${q}`;
}

/** Zahl abgelegter Briefe insgesamt (`alle`) und je Kategorie; `undefined`, solange sie laedt oder fehlschlug.
 *  Der Schluessel beginnt mit „dokumente“: wird ein Brief abgelegt, laden die Zahlen mit den Listen neu. */
export function useArchivAnzahlen(kategorien: Kategorie[] | undefined) {
  const ausKategorien = zahlQuelle(kategorien) === "kategorien";
  const ids = [undefined, ...(ausKategorien ? [] : (kategorien ?? []).map((k) => k.id))];
  const ergebnisse = useQueries({
    queries: ids.map((id) => ({
      queryKey: ["dokumente", "anzahl", id ?? "alle"],
      queryFn: ({ signal }: { signal: AbortSignal }) => api<Liste<Dokument>>(anzahlPfad(id), { signal }),
      select: (l: Liste<Dokument>) => l.gesamt,
    })),
  });
  const [alle, ...je] = ergebnisse.map((e) => e.data);
  if (ausKategorien) return { alle, je: new Map((kategorien ?? []).map((k) => [k.id, k.anzahl_archiviert])) };
  return { alle, je: new Map(ids.slice(1).map((id, i) => [id as string, je[i]])) };
}
